import { useEffect, useRef } from 'react';
import {
  getPusherClient,
  isPusherConfigured,
  PUSHER_CHANNEL_NAME,
  PUSHER_PRESS_EVENT,
  PUSHER_RESET_EVENT,
} from '../lib/pusherClient';
import type { SurveyPhase } from '../types/survey';

interface PusherControlOptions {
  phase: SurveyPhase;
  operatorOpen: boolean;
  onReset: () => void;
  onStart: () => void;
  onAnswer: (position: number) => void;
}

interface PressPayload {
  position: number;
}

/** Mirrors the numpad 1/2/3 behaviour of useKioskControls, driven remotely from /controle.html. */
export function usePusherControls(options: PusherControlOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!isPusherConfigured) return;

    const pusher = getPusherClient();
    const channel = pusher.subscribe(PUSHER_CHANNEL_NAME);

    function handlePress(payload: PressPayload) {
      const { phase, operatorOpen, onAnswer, onStart } = optionsRef.current;
      const position = Number(payload?.position);
      if (!Number.isInteger(position) || operatorOpen) return;

      if (phase === 'question') {
        onAnswer(position);
      } else if (phase === 'start' || (phase === 'result' && position === 1)) {
        onStart();
      }
    }

    function handleReset() {
      const { operatorOpen, onReset } = optionsRef.current;
      if (operatorOpen) return;
      onReset();
    }

    channel.bind(PUSHER_PRESS_EVENT, handlePress);
    channel.bind(PUSHER_RESET_EVENT, handleReset);

    return () => {
      channel.unbind(PUSHER_PRESS_EVENT, handlePress);
      channel.unbind(PUSHER_RESET_EVENT, handleReset);
      pusher.unsubscribe(PUSHER_CHANNEL_NAME);
    };
  }, []);
}
